import { useCallback, useSyncExternalStore } from 'react';
import { DRAFT_MESSAGES, REPLY_STATE } from '../Constants/constantValues';

class DraftMessagesStore {
    constructor() {
        this.listeners = new Set();
        this.draftMessages = this.getInitialState();
        this.replyStates = this.getReplyStates(); 
        this.snapshot = { 
            draftMessages: this.draftMessages,
            replyStates: this.replyStates
        };
    }

    getInitialState() {
        try {
            const item = window.localStorage.getItem(DRAFT_MESSAGES);
            return item ? JSON.parse(item) : {};
        } catch (error) {
            console.warn(`Error reading localStorage key "${DRAFT_MESSAGES}":`, error);
            return {};
        }
    }

    getReplyStates() {
        try {
            const item = window.localStorage.getItem(REPLY_STATE);
            return item ? JSON.parse(item) : {};
        } catch (error) {
            console.warn(`Error reading localStorage key "${REPLY_STATE}":`, error);
            return {};
        }
    }

    getSnapshot = () => {
        return this.snapshot;
    };

    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    updateDraftMessages = (newDraftMessages) => {
        this.draftMessages = newDraftMessages;

        try {
            if (Object.keys(newDraftMessages).length === 0) {
                window.localStorage.removeItem(DRAFT_MESSAGES);
            } else {
                window.localStorage.setItem(DRAFT_MESSAGES, JSON.stringify(newDraftMessages));
            }
        } catch (error) {
            console.warn(`Error setting localStorage key "${DRAFT_MESSAGES}":`, error);
        }

        this.snapshot = {
            draftMessages: this.draftMessages,
            replyStates: this.replyStates
        };

        this.listeners.forEach(listener => listener());
    };

    updateReplyStates = (newReplyStates) => {
        this.replyStates = newReplyStates;

        try {
            if (Object.keys(newReplyStates).length === 0) {
                window.localStorage.removeItem(REPLY_STATE);
            } else {
                window.localStorage.setItem(REPLY_STATE, JSON.stringify(newReplyStates));
            }
        } catch (error) {
            console.warn(`Error setting localStorage key "${REPLY_STATE}":`, error);
        }

        this.snapshot = {
            draftMessages: this.draftMessages,
            replyStates: this.replyStates
        };

        this.listeners.forEach(listener => listener());
    };

    saveDraft = (conversationId, message) => {
        if (!conversationId) return;

        const trimmedMessage = message?.trim() || '';
        const updated = { ...this.draftMessages };

        if (trimmedMessage.length === 0) {
            delete updated[conversationId];
        } else {
            updated[conversationId] = trimmedMessage;
        }

        this.updateDraftMessages(updated);
    };

    clearDraft = (conversationId) => {
        if (!conversationId) return;

        const updated = { ...this.draftMessages };
        delete updated[conversationId];
        this.updateDraftMessages(updated);
    };

    getDraft = (conversationId) => {
        if (!conversationId) return '';
        return this.draftMessages[conversationId] || '';
    };

    hasDraft = (conversationId) => {
        if (!conversationId) return false;
        const draft = this.draftMessages[conversationId];
        return draft && draft.trim().length > 0;
    };

    saveReplyState = (conversationId, messageId, messageObject, replyText) => {
        if (!conversationId) return;

        const updated = { ...this.replyStates };
        updated[conversationId] = {
            messageId,
            messageObject,
            replyText: replyText?.trim() || ''
        };

        this.updateReplyStates(updated);
    };

    clearReplyState = (conversationId) => {
        if (!conversationId) return;

        const updated = { ...this.replyStates };
        delete updated[conversationId];
        this.updateReplyStates(updated);
    };

    getReplyState = (conversationId) => {
        if (!conversationId) return null;
        return this.replyStates[conversationId] || null;
    };

    hasReplyState = (conversationId) => {
        if (!conversationId) return false;
        return !!this.replyStates[conversationId];
    };
}

const draftMessagesStore = new DraftMessagesStore();

const useDraftMessages = () => {
    useSyncExternalStore(
        draftMessagesStore.subscribe,
        draftMessagesStore.getSnapshot
    );

    const getDraftMessage = useCallback((conversationId) => {
        return draftMessagesStore.getDraft(conversationId);
    }, []);

    const saveDraftMessage = useCallback((conversationId, message) => {
        draftMessagesStore.saveDraft(conversationId, message);
    }, []);

    const clearDraftMessage = useCallback((conversationId) => {
        draftMessagesStore.clearDraft(conversationId);
    }, []);

    const hasDraftMessage = useCallback((conversationId) => {
        return draftMessagesStore.hasDraft(conversationId);
    }, []);

    const saveReplyState = useCallback((conversationId, messageId, messageObject, replyText) => {
        draftMessagesStore.saveReplyState(conversationId, messageId, messageObject, replyText);
    }, []);

    const clearReplyState = useCallback((conversationId) => {
        draftMessagesStore.clearReplyState(conversationId);
    }, []);

    const getReplyState = useCallback((conversationId) => {
        return draftMessagesStore.getReplyState(conversationId);
    }, []);

    const hasReplyState = useCallback((conversationId) => {
        return draftMessagesStore.hasReplyState(conversationId);
    }, []);

    const getAllDraftConversationIds = useCallback(() => {
        return Object.keys(draftMessagesStore.draftMessages).map(id => parseInt(id));
    }, []);

    return {
        getDraftMessage,
        saveDraftMessage,
        clearDraftMessage,
        hasDraftMessage,
        saveReplyState,
        clearReplyState,
        getReplyState,
        hasReplyState,
        getAllDraftConversationIds
    };
};

export default useDraftMessages;